import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import RightBar from "../../../../layouts/Dashboard/components/rightbar";
import AllOrderDetailsRightBar from "../../../../pages/all-orders/components/rightsidebar/rightsidebar_details";
import { OrderDetailsData } from "../../../../contexts";
import GetAllOrders from "../../../../hooks/fetch-hooks/fetch-all/get-all-orders";
import { GetSortBy } from "../../../../utils/sortby";
import { ALL_ORDER_TYPE, CHRONICITY_MAP, PRESORTED_ORDER_TYPE, STRIPS_ORDER_TYPE } from "../../../../utils/constants";
import LegacyOrdersTableInstance from "./table-instance";
import ColumnsModal from "./ColumnsModal";
import { loadHiddenColumns, saveHiddenColumns } from "./columnPrefs";
import "./scroll.css";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import { ORDER_STATUS_FILTERS } from "../../../constants/order-status-filters";


// The shared chip groups (src/v2/constants/order-status-filters.js), in this page's
// original { value: "A,B", label } shape -- identical values, so selection and the
// `status` param are unchanged.
const STATUS_OPTIONS = ORDER_STATUS_FILTERS.map((filter) => ({ value: filter.statuses.join(","), label: filter.label }));

const CHRONICITY_OPTIONS = [
    { value: "NONE", label: "None" },
    ...Object.keys(CHRONICITY_MAP).map((key) => ({ value: key, label: CHRONICITY_MAP[key] })),
];

/** Genuinely backend-sortable new columns — verified DATE/FLOAT model attributes. */
const EXTRA_SORT_MAP = { createdAt: "createdAt", startDate: "startDate", total: "total" };

function getSortBy(accessorId) {
    return EXTRA_SORT_MAP[accessorId] || GetSortBy(accessorId);
}

async function fetchOrders({ page, size, search, order, orderBy, orderType, selectedStatus, from, to, createdFrom, createdTill, chronicity, includeFinance }) {
    const type = orderType === PRESORTED_ORDER_TYPE ? "PRESORTED" : orderType === STRIPS_ORDER_TYPE ? "STRIPS" : "ALL";
    return GetAllOrders(
        {
            page,
            size,
            patientName: search,
            userName: search,
            order,
            orderBy,
            status: selectedStatus.map((status) => status).join(","),
            type,
            endDateAt: from || null,
            endDateTill: to || null,
            chronicity: chronicity?.length > 0 ? chronicity.join(",") : null,
            createdAt: createdFrom || null,
            createdTill: createdTill || null,
            // Opt-in per console: the Sales Console's finance columns need the rollup,
            // ops does not, and it costs an extra query per page. Undefined is omitted
            // by axios, so ops sends exactly the request it sent before.
            includeFinance: includeFinance || undefined,
        },
        ""
    );
}

const FiltersModal = ({ filterData, setFilterData, onClose }) => {
    const [tempFilterData, setTempFilterData] = useState({ ...filterData });

    const applyFilters = () => {
        setFilterData({ ...tempFilterData });
        onClose();
    };

    const clearFilters = () => {
        const cleared = { from: "", to: "", createdFrom: "", createdTill: "", pageSize: DEFAULT_PAGE_SIZE, chronicity: [] };
        setTempFilterData(cleared);
        setFilterData(cleared);
        onClose();
    };

    return (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-[500]">
            <div className="w-full flex flex-col justify-center">
                <div style={{ backgroundColor: "white", width: "36vw", marginLeft: "32vw" }} className="flex flex-col gap-4 justify-center p-4">
                    <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold">Filters</span>
                        <button onClick={onClose}>×</button>
                    </div>
                    <hr />
                    <div className="flex flex-col gap-2">
                        <label className="font-bold">Page Size</label>
                        <input
                            type="number"
                            className="px-6 w-full font-sans py-4 col-span-2 bg-white-50"
                            value={tempFilterData.pageSize}
                            onChange={(e) => setTempFilterData({ ...tempFilterData, pageSize: e.target.value })}
                        />
                        <label className="font-bold">Created At Date Range</label>
                        <div className="flex gap-4 items-center">
                            <div>
                                <label className="text-sm">From</label>
                                <input
                                    type="date"
                                    className="border rounded-md p-1 ml-2"
                                    value={tempFilterData.createdFrom}
                                    onChange={(e) => setTempFilterData({ ...tempFilterData, createdFrom: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-sm">To</label>
                                <input
                                    type="date"
                                    className="border rounded-md p-1 ml-2"
                                    value={tempFilterData.createdTill}
                                    disabled={!tempFilterData.createdFrom}
                                    onChange={(e) =>
                                        setTempFilterData({
                                            ...tempFilterData,
                                            createdTill: moment(tempFilterData.createdFrom).isAfter(e.target.value) ? tempFilterData.createdFrom : e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <label className="font-bold">End Date Range</label>
                        <div className="flex gap-4 items-center">
                            <div>
                                <label className="text-sm">From</label>
                                <input
                                    type="date"
                                    className="border rounded-md p-1 ml-2"
                                    value={tempFilterData.from}
                                    onChange={(e) => setTempFilterData({ ...tempFilterData, from: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-sm">To</label>
                                <input
                                    type="date"
                                    className="border rounded-md p-1 ml-2"
                                    value={tempFilterData.to}
                                    disabled={!tempFilterData.from}
                                    onChange={(e) =>
                                        setTempFilterData({
                                            ...tempFilterData,
                                            to: moment(tempFilterData.from).isAfter(e.target.value) ? tempFilterData.from : e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="font-bold">Chronicity</label>
                        <div className="flex flex-wrap gap-2">
                            {CHRONICITY_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    className={`text-sm px-3 py-1 rounded-full border ${
                                        tempFilterData.chronicity?.includes(option.value)
                                            ? "bg-[#00A48B] text-white-50 border-[#00A48B]"
                                            : "bg-white text-gray-700 border-gray-300"
                                    }`}
                                    onClick={() =>
                                        setTempFilterData((prev) => {
                                            const current = prev.chronicity || [];
                                            const updated = current.includes(option.value)
                                                ? current.filter((v) => v !== option.value)
                                                : [...current, option.value];
                                            return { ...prev, chronicity: updated };
                                        })
                                    }
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <button className="bg-gray-200 px-4 py-1 rounded-md" onClick={clearFilters}>
                            Clear
                        </button>
                        <button className="bg-[#00A48B] text-white-50 px-4 py-1 rounded-md" onClick={applyFilters}>
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

FiltersModal.propTypes = {
    filterData: PropTypes.object.isRequired,
    setFilterData: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

/**
 * The orders queue, shared by /v2/ops/orders and /v2/sales/orders.
 *
 * Every prop defaults to the ops behaviour, so the ops route needs no arguments and is
 * byte-for-byte what it was. The Sales Console passes its own column builder, its own
 * localStorage key for the column picker, and includeFinance -- see
 * src/v2/sales/pages/orders/index.jsx.
 *
 * @param {Function} extraColumns builder for the columns spliced into legacy's own set
 * @param {boolean}  includeFinance request pillup-api's finance rollup for each row
 * @param {Function} loadHidden / saveHidden column-picker persistence, keyed per console
 */
function LegacyOrdersPage({
    extraColumns,
    includeFinance = false,
    loadHidden = loadHiddenColumns,
    saveHidden = saveHiddenColumns,
    columnGroups,
    hideableIds,
    functionalityKey,
}) {
    const [searchParams, setSearchParams] = useSearchParams();

    // Page-level wiring src/pages/all-orders/index.jsx does around OrdersTable.
    const [data, setData] = useState();
    const [rightBarOpen, setRightBarOpen] = useState({ name: "v2-orders-legacy", open: false });
    const [OrderType, setOrderType] = useState(ALL_ORDER_TYPE);
    const changeRightBarOpen = () => {
        if (!rightBarOpen.open) setRightBarOpen({ ...rightBarOpen, open: true });
    };
    const changeRightBarClose = () => setRightBarOpen({ ...rightBarOpen, open: false });

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageSort, setPageSort] = useState([]);
    const [pageindex, setPageIndex] = useState(Number(searchParams.get("page")) || 1);
    const [value, setValue] = useState(searchParams.get("search") || "");
    const [debouncedSearch, setDebouncedSearch] = useState(value);
    const [order, setOrder] = useState(undefined);
    const [orderBy, setOrderBy] = useState(undefined);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [columnsOpen, setColumnsOpen] = useState(false);
    // Lazy initialiser: localStorage is read once on mount, not on every render.
    const [hiddenColumns, setHiddenColumns] = useState(() => loadHidden());
    const [filterData, setFilterData] = useState({
        from: searchParams.get("from") || "",
        to: searchParams.get("to") || "",
        pageSize: Number(searchParams.get("size")) || DEFAULT_PAGE_SIZE,
        createdFrom: searchParams.get("createdFrom") || "",
        createdTill: searchParams.get("createdTill") || "",
        chronicity: searchParams.get("chronicity") ? searchParams.get("chronicity").split(",") : [],
    });

    useEffect(() => {
        if (pageSort.length > 0) {
            setOrder(pageSort[0].desc ? "DESC" : "ASC");
            setOrderBy(getSortBy(pageSort[0].id));
        } else {
            setOrder(undefined);
            setOrderBy(undefined);
        }
    }, [pageSort]);

    const getData = async () => {
        const fetchedData = await fetchOrders({
            page: pageindex,
            size: filterData.pageSize,
            search: debouncedSearch,
            order,
            orderBy,
            orderType: OrderType,
            selectedStatus,
            from: filterData.from,
            to: filterData.to,
            createdFrom: filterData.createdFrom,
            createdTill: filterData.createdTill,
            chronicity: filterData.chronicity,
            includeFinance,
        });
        const rows = fetchedData?.data?.data?.rows || [];
        setTableData(
            rows.map((row) => ({
                ...row,
                daysLeft: Math.floor((new Date(row.endDate) - new Date()) / (1000 * 60 * 60 * 24) + 2),
            }))
        );
        setTotal(fetchedData?.data?.data?.count || 0);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(value);
            setPageIndex(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [value]);

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageindex, debouncedSearch, order, orderBy, selectedStatus, OrderType, filterData]);

    useEffect(() => {
        setSearchParams({
            page: pageindex,
            size: filterData.pageSize,
            search: value,
            from: filterData.from,
            to: filterData.to,
            createdFrom: filterData.createdFrom,
            createdTill: filterData.createdTill,
            chronicity: filterData.chronicity?.join(",") || "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageindex, value, filterData]);

    const maxValues = Math.ceil(total / filterData.pageSize) || 1;

    const handleDeleteRow = (id) => setTableData((prev) => prev.filter((item) => item.id !== id));

    return (
        <div className="orders-legacy-page p-7 h-full overflow-y-scroll bg-gray-50">
            <RightBar rightBarOpenAction={changeRightBarOpen} rightBarCloseAction={changeRightBarClose} value={rightBarOpen.open} data={data}>
                <AllOrderDetailsRightBar {...data} />
            </RightBar>
            <OrderDetailsData.Provider
                value={{
                    data,
                    setData,
                    // `functionalityKey` overrides ONLY what TableLayout looks up in
                    // COLUMNS_FUCTIONALITIES (src/components/orderTable/Constants.js) --
                    // i.e. which toolbar and row buttons render. The tab state below is
                    // untouched, so All/Presorted/Strips still drive the fetch.
                    //
                    // Side effect, accepted: TableLayout's Excel switch keys off the same
                    // value, so an overriding console always takes the `default:` branch
                    // (ORDER_DOWNLOAD_COLUMN) rather than the Presorted-specific one.
                    // That is the branch ALL_ORDERS and PHARMACY_ORDERS already take.
                    OrderType: functionalityKey || OrderType,
                    setOrderType,
                    isReorder: false,
                }}
            >
                <div className="flex gap-4 items-center relative">
                    <input
                        value={value}
                        className="px-6 font-sans w-[80%] bg-slate-100 py-4"
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={`${total} records...`}
                        style={{ fontSize: "1.1rem", border: "0" }}
                    />
                </div>
                <div className="flex justify-start items-center my-[1rem] gap-2">
                    <button
                        onClick={() => setOrderType(ALL_ORDER_TYPE)}
                        className={`${OrderType === ALL_ORDER_TYPE ? "bg-elm-500 text-white-50" : "bg-transparent text-elm-500"} border-[1px] border-elm-500 px-4 hover:bg-elm-500 hover:text-white-50 font-semibold py-2 rounded-[9px]`}
                    >
                        All Orders
                    </button>
                    <button
                        onClick={() => setOrderType(PRESORTED_ORDER_TYPE)}
                        className={`${OrderType === PRESORTED_ORDER_TYPE ? "bg-elm-500 text-white-50" : "bg-transparent text-elm-500"} border-[1px] border-elm-500 px-4 hover:bg-elm-500 hover:text-white-50 font-semibold py-2 rounded-[9px]`}
                    >
                        Presorted
                    </button>
                    <button
                        onClick={() => setOrderType(STRIPS_ORDER_TYPE)}
                        className={`${OrderType === STRIPS_ORDER_TYPE ? "bg-elm-500 text-white-50" : "bg-transparent text-elm-500"} border-[1px] border-elm-500 px-4 hover:bg-elm-500 hover:text-white-50 font-semibold py-2 rounded-[9px]`}
                    >
                        Strips
                    </button>
                </div>
                <button className="bg-elm-500 text-white-50 px-4 py-1 rounded-md mr-3" onClick={() => setFilterOpen(true)}>
                    Filters
                </button>
                <button
                    className="border-[1px] border-elm-500 text-elm-500 px-4 py-1 rounded-md mr-3"
                    onClick={() => setColumnsOpen(true)}
                >
                    Columns
                </button>
                {(filterData.from || filterData.to || filterData.createdFrom || filterData.createdTill || filterData.chronicity?.length > 0) && (
                    <button
                        className="bg-red-500 text-white-50 px-4 py-1 rounded-md"
                        onClick={() => setFilterData({ from: "", to: "", createdFrom: "", createdTill: "", pageSize: DEFAULT_PAGE_SIZE, chronicity: [] })}
                    >
                        Clear Filters
                    </button>
                )}
                {filterOpen && <FiltersModal filterData={filterData} setFilterData={setFilterData} onClose={() => setFilterOpen(false)} />}
                {columnsOpen && (
                    <ColumnsModal
                        hiddenColumns={hiddenColumns}
                        groups={columnGroups}
                        hideableIds={hideableIds}
                        onApply={(next) => {
                            setHiddenColumns(next);
                            saveHidden(next);
                            setColumnsOpen(false);
                        }}
                        onClose={() => setColumnsOpen(false)}
                    />
                )}
                <div className="flex gap-4 items-center my-2 justify-start relative flex-wrap">
                    {STATUS_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            className={`text-[10px] px-2 py-1 rounded-[10px] text-[#333] flex items-center gap-2 ${
                                selectedStatus.includes(option.value) ? "bg-elm-500 text-white-50" : "bg-gray-200 text-[#333]"
                            }`}
                            onClick={() =>
                                setSelectedStatus((prev) =>
                                    prev.includes(option.value) ? prev.filter((status) => status !== option.value) : [...prev, option.value]
                                )
                            }
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <LegacyOrdersTableInstance
                    tableData={tableData}
                    changeRightBarOpen={changeRightBarOpen}
                    pageCount={total}
                    setPageSort={setPageSort}
                    pageSort={pageSort}
                    handleDeleteRow={handleDeleteRow}
                    refetchOrders={getData}
                    hiddenColumns={hiddenColumns}
                    extraColumns={extraColumns}
                />
                <div className="btn_div">
                    <button onClick={() => setPageIndex((count) => Number(count) - 1)} disabled={pageindex <= 1} className="previous">
                        Previous
                    </button>
                    <span className="pageindex">{`Page ${pageindex} of ${maxValues}`}</span>
                    <button onClick={() => setPageIndex((count) => Number(count) + 1)} disabled={pageindex >= maxValues} className="next">
                        Next
                    </button>
                </div>
            </OrderDetailsData.Provider>
        </div>
    );
}

// Every prop is optional and defaults to the ops behaviour, so /v2/ops/orders renders
// this component with no arguments at all. See the doc comment on LegacyOrdersPage.
LegacyOrdersPage.propTypes = {
    extraColumns: PropTypes.func,
    includeFinance: PropTypes.bool,
    loadHidden: PropTypes.func,
    saveHidden: PropTypes.func,
    columnGroups: PropTypes.array,
    hideableIds: PropTypes.arrayOf(PropTypes.string),
    functionalityKey: PropTypes.string,
};

export default LegacyOrdersPage;
